import React, { useState } from 'react';
import { Input, InputNumber, Button, Modal, Radio, Spin, Table, Checkbox } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useBaseSalaryForm } from './hooks/useBaseSalaryForm';
import LookupField from '../../components/LookupField';

const P9232Form: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    empID, setEmpID,
    taxDeduction, setTaxDeduction,
    salaryMethod, setSalaryMethod,
    bank, setBank,
    bankAccount, setBankAccount,
    payMethod, setPayMethod,
    dependents, setDependents,
    laborInsurance, setLaborInsurance,
    healthInsurance, setHealthInsurance,
    healthDependents, setHealthDependents,
    pensionSystem, setPensionSystem,
    voluntaryContrib, setVoluntaryContrib,
    contribPercent, setContribPercent,
    monthlyPension, setMonthlyPension,
    details, addDetail, removeDetail, updateDetail,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
  } = useBaseSalaryForm();

  const [searchText, setSearchText] = useState('');

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除員工「${current.f9420_01}」的薪資設定？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleQuickSearch = () => {
    if (searchText.trim()) quickSearch(searchText.trim());
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  const detailColumns = [
    { title: 'NO.', dataIndex: 'f9421_02', width: 50, render: (v: number, _: any, i: number) => v ?? i + 1 },
    {
      title: '薪資代號', dataIndex: 'f9421_11', width: 100,
      render: (v: string, _: any, i: number) => (
        <Input value={v} disabled={!isEditing} maxLength={6}
          onChange={e => updateDetail(i, 'f9421_11', e.target.value)} />
      ),
    },
    {
      title: '名稱', dataIndex: 'f9421_11a', width: 150,
      render: (v: string, _: any, i: number) => (
        <Input value={v} disabled={!isEditing} maxLength={30}
          onChange={e => updateDetail(i, 'f9421_11a', e.target.value)} />
      ),
    },
    {
      title: '固定金額', dataIndex: 'f9421_12', width: 120,
      render: (v: number, _: any, i: number) => (
        <InputNumber value={v} disabled={!isEditing} style={{ width: '100%' }}
          onChange={val => updateDetail(i, 'f9421_12', val ?? 0)} />
      ),
    },
    {
      title: '說明', dataIndex: 'f9421_13',
      render: (v: string, _: any, i: number) => (
        <Input value={v} disabled={!isEditing} maxLength={60}
          onChange={e => updateDetail(i, 'f9421_13', e.target.value)} />
      ),
    },
    ...(isEditing ? [{
      title: '', width: 40,
      render: (_: any, __: any, i: number) => (
        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeDetail(i)} />
      ),
    }] : []),
  ];

  return (
    <Spin spinning={loading}>
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          個人基本薪資設定 (pw9232f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick Search */}
        <div className="flex items-center gap-2 mb-4">
          <Input placeholder="輸入員工代號查詢" value={searchText}
            onChange={e => setSearchText(e.target.value)} onPressEnter={handleQuickSearch}
            disabled={isEditing} style={{ width: 200 }} maxLength={8} />
          <Button onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Header Fields */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">員工代號 (F9420_01)</label>
            <Input value={empID} onChange={e => setEmpID(e.target.value)}
              maxLength={8} disabled={mode !== 'add'} style={{ width: 200 }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">部門名稱 (F106_11)</label>
            <Input value={(current as any)?.f106_11 || ''} disabled style={{ width: 200 }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">扣繳所得稅 (F9420_11)</label>
            <Radio.Group value={taxDeduction} onChange={e => setTaxDeduction(e.target.value)} disabled={!isEditing}>
              <Radio value={1}>是</Radio><Radio value={2}>否</Radio>
            </Radio.Group>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">計薪方式 (F9420_12)</label>
            <Radio.Group value={salaryMethod} onChange={e => setSalaryMethod(e.target.value)} disabled={!isEditing}>
              <Radio value={1}>月薪</Radio><Radio value={2}>日薪</Radio><Radio value={3}>時薪</Radio>
            </Radio.Group>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">領薪方式 (F9420_15)</label>
            <Radio.Group value={payMethod} onChange={e => setPayMethod(e.target.value)} disabled={!isEditing}>
              <Radio value={1}>現金</Radio><Radio value={2}>轉帳</Radio>
            </Radio.Group>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">撥款銀行 (F9420_13)</label>
            <Input value={bank} onChange={e => setBank(e.target.value)}
              maxLength={20} disabled={!isEditing} style={{ width: 200 }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">撥款帳號 (F9420_14)</label>
            <Input value={bankAccount} onChange={e => setBankAccount(e.target.value)}
              maxLength={30} disabled={!isEditing} style={{ width: 250 }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">扶養人數 (F9420_17)</label>
            <InputNumber value={dependents} onChange={v => setDependents(v ?? 0)}
              min={0} disabled={!isEditing} style={{ width: 100 }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">勞保薪資 (F9420_21)</label>
            <LookupField lookupType="salaryTier" value={String(laborInsurance || '')}
              onChange={(code) => setLaborInsurance(Number(code) || 0)}
              disabled={!isEditing} style={{ width: 150 }} title="勞保薪資級距" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">健保薪資 (F9420_23)</label>
            <LookupField lookupType="salaryTier" value={String(healthInsurance || '')}
              onChange={(code) => setHealthInsurance(Number(code) || 0)}
              disabled={!isEditing} style={{ width: 150 }} title="健保薪資級距" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">健保負擔人數 (F9420_24)</label>
            <InputNumber value={healthDependents} onChange={v => setHealthDependents(v ?? 0)}
              min={0} disabled={!isEditing} style={{ width: 100 }} />
          </div>
        </div>

        {/* Pension Section */}
        <div className="border rounded p-4 mb-4 bg-gray-50">
          <h3 className="text-sm font-bold mb-3">退休金設定</h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">退休金制度 (F9420_34)</label>
              <Checkbox
                checked={pensionSystem === 1}
                onChange={e => setPensionSystem(e.target.checked ? 1 : 2)}
                disabled={!isEditing}
              >新制</Checkbox>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">退休金扣繳 (F9420_35)</label>
              <Checkbox
                checked={!!(current as any)?.f9420_35}
                disabled={!isEditing}
              >啟用</Checkbox>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">自願提繳 (F9420_31)</label>
              <Radio.Group value={voluntaryContrib} onChange={e => setVoluntaryContrib(e.target.value)} disabled={!isEditing}>
                <Radio value={1}>是</Radio><Radio value={2}>否</Radio>
              </Radio.Group>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">提繳百分比 % (F9420_37)</label>
              <LookupField lookupType="salaryTier" value={String(contribPercent || '')}
                onChange={(code) => setContribPercent(Number(code) || 0)}
                disabled={!isEditing} style={{ width: 100 }} title="提繳比率" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">退休金月繳工資 (F9420_38)</label>
              <LookupField lookupType="salaryTier" value={String(monthlyPension || '')}
                onChange={(code) => setMonthlyPension(Number(code) || 0)}
                disabled={!isEditing} style={{ width: 150 }} title="月繳工資級距" />
            </div>
          </div>
        </div>

        {/* Details Table */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold">薪資明細 ({details.length} 筆)</h3>
            {isEditing && (
              <Button size="small" icon={<PlusOutlined />} onClick={addDetail}
                disabled={details.length >= 99}>新增明細</Button>
            )}
          </div>
          <Table dataSource={details} columns={detailColumns} pagination={false}
            size="small" rowKey={(_, i) => String(i)} scroll={{ y: 300 }} />
        </div>

        {/* Audit Info */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-4 space-y-1">
            <div>建立人員: {current.f9420_91} | 建立日期: {current.f9420_92 || '-'}</div>
            <div>修改人員: {current.f9420_93} | 異動日期: {current.f9420_18 || '-'}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default P9232Form;
